import {
  useTaxStore,
  useCurrentTaxInput,
  useRecurringProjection,
} from "../store/useTaxStore";
import { cn } from "@/lib/utils";
import AmountInput from "@/components/AmountInput";

function fmt(n: number) {
  return n.toLocaleString("th-TH");
}

function NumberInput({
  label,
  sublabel,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  sublabel?: string;
  value: number;
  onChange?: (v: number) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium text-foreground leading-snug">
          {label}
        </div>
        {sublabel && (
          <div className="text-[12px] text-muted-foreground mt-0.5">
            {sublabel}
          </div>
        )}
      </div>
      <div className="relative flex-shrink-0">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground pointer-events-none">
          ฿
        </span>
        <AmountInput
          value={value ? String(value) : ""}
          readOnly={readOnly}
          onChange={(v) => onChange?.(Number(v) || 0)}
          placeholder="0"
          className={cn(
            "w-36 pl-7 pr-3 py-2 text-right text-[14px] rounded-lg border transition-colors",
            "font-sans [font-variant-numeric:tabular-nums]",
            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
            "bg-card border-border text-foreground",
            "focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary",
            readOnly && "bg-muted cursor-default text-muted-foreground",
          )}
        />
      </div>
    </div>
  );
}

export default function IncomeSection() {
  const input = useCurrentTaxInput();
  const { updateInput } = useTaxStore();
  const projection = useRecurringProjection();

  const emp40_1Deduction = Math.min(input.employmentIncome * 0.5, 100_000);
  const freelanceDeduction =
    input.freelanceMethod === "standard"
      ? input.freelanceRevenue * 0.6
      : Math.max(0, input.freelanceActualExpenses);

  return (
    <div className="rounded-[14px] border border-border bg-card shadow-sm">
      <div className="px-5 pt-5 pb-1">
        <h2 className="text-[15px] font-semibold text-foreground">Income</h2>
        <p className="text-[12px] text-muted-foreground mt-0.5">
          Enter your annual income for this tax year
        </p>
      </div>

      <div className="px-5 divide-y divide-border">
        {/* 40(1) Employment */}
        <div>
          <div className="py-2">
            <div className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide">
              Section 40(1) — Salary / Wages
            </div>
          </div>
          <NumberInput
            label="Employment Income"
            sublabel="Salary, bonus, overtime — full year total"
            value={input.employmentIncome}
            onChange={(v) => updateInput("employmentIncome", v)}
          />
          {projection && projection.employment40_1 > 0 && (
            <p className="text-[11px] text-muted-foreground -mt-2 pb-1">
              From recurring income: ฿{fmt(projection.employment40_1)}
            </p>
          )}
          <NumberInput
            label="Expense deduction 50% (max ฿100,000)"
            value={emp40_1Deduction}
            readOnly
            sublabel={`Auto-calculated = ฿${fmt(emp40_1Deduction)}`}
          />
        </div>

        {/* 40(8) Freelance */}
        <div>
          <div className="py-2">
            <div className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide">
              Section 40(8) — Other Income / Freelance
            </div>
          </div>

          <NumberInput
            label="Total Revenue"
            sublabel="Business, professional, or self-employed income"
            value={input.freelanceRevenue}
            onChange={(v) => updateInput("freelanceRevenue", v)}
          />
          {projection && projection.freelance40_8 > 0 && (
            <p className="text-[11px] text-muted-foreground -mt-2 pb-1">
              From recurring income: ฿{fmt(projection.freelance40_8)}
            </p>
          )}

          {/* Method toggle */}
          <div className="py-3">
            <div className="text-[14px] font-medium text-foreground mb-2">
              Expense Deduction Method
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => updateInput("freelanceMethod", "standard")}
                className={cn(
                  "flex-1 py-2 px-3 rounded-lg text-[13px] font-medium border transition-colors",
                  input.freelanceMethod === "standard"
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border hover:border-primary/50",
                )}
              >
                Standard 60%
              </button>
              <button
                onClick={() => updateInput("freelanceMethod", "actual")}
                className={cn(
                  "flex-1 py-2 px-3 rounded-lg text-[13px] font-medium border transition-colors",
                  input.freelanceMethod === "actual"
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border hover:border-primary/50",
                )}
              >
                Actual Expenses
              </button>
            </div>
          </div>

          {input.freelanceMethod === "actual" && (
            <NumberInput
              label="Actual Expenses"
              sublabel="Expenses directly related to earning income"
              value={input.freelanceActualExpenses}
              onChange={(v) => updateInput("freelanceActualExpenses", v)}
            />
          )}

          <NumberInput
            label={
              input.freelanceMethod === "standard"
                ? "Standard 60% deduction"
                : "Actual expense deduction"
            }
            value={freelanceDeduction}
            readOnly
            sublabel={`Auto-calculated = ฿${fmt(freelanceDeduction)}`}
          />
        </div>

        {/* Withholding */}
        <div>
          <div className="py-2">
            <div className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide">
              Withholding Tax Credit (ภาษีหัก ณ ที่จ่าย)
            </div>
          </div>
          <NumberInput
            label="Withholding Tax (annual total)"
            sublabel="From your withholding tax certificate"
            value={input.withholdingTax}
            onChange={(v) => updateInput("withholdingTax", v)}
          />
        </div>
      </div>

      <div className="h-4" />
    </div>
  );
}
